import React, { useState, useEffect } from "react";
import Button from "../components/UI/button";
import { country } from "../constants/country";
import { getAllUsers, addUser, updateUser } from "../services/settings";
import { useNavigate } from "react-router-dom";
import Loader from "../components/UI/loader";
import Popup from "../components/UI/popup";
import { googleSignOut } from "../services/googleSignUp";

const inputClass =
  "w-full px-4 py-3 bg-gray-50 rounded-xl border border-purple-200 outline-none text-sm text-gray-900 placeholder-gray-400 focus:border-primary";

function Settings() {
  const [userData, setUserData] = useState({
    companyName: "",
    country: "",
    maskName: false,
  });
  const [user, setUser] = useState();
  const [loading, setLoader] = useState(false);
  const [open, setOpen] = useState(false);
  const email = localStorage.getItem("email");
  const navigate = useNavigate();

  useEffect(() => {
    getAllUsers(setLoader, setUser, email);
  }, []);

  useEffect(() => {
    if (user && user.length > 0) {
      handleAdd();
      const currentUser = user.find((u) => u.email === email);
      if (currentUser) {
        setUserData({
          ...userData,
          companyName: currentUser.companyName || "",
          country: currentUser.country || "",
          maskName: currentUser.maskName || false,
        });
      }
    }
  }, [user]);

  async function handleAdd() {
    const filteredData = user.filter((i) => i.email === email);
    if (filteredData.length === 0) {
      addUser(setLoader, setUser, email);
    }
  }

  function handleData(e) {
    const data = { ...userData };
    const { name, value } = e.target;
    data[name] = value;
    setUserData(data);
  }

  const isValid = userData.companyName && userData.country;

  function handleSubmit(e) {
    e.preventDefault();
    const currentUserData = user.find((u) => u.email === email);
    const data = {
      companyName: userData.companyName,
      country: userData.country,
      maskName: userData.maskName,
      email: email,
    };
    updateUser(setLoader, data, email, setOpen, currentUserData?.id);
  }

  const onClose = () => {
    setOpen(false);
    navigate("/dashboard");
  };

  const handleLogout = async () => {
    await googleSignOut();
    localStorage.removeItem("email");
    navigate("/");
  };


  return (
    <div className="max-w-md mx-auto">
      <div className="px-5 pt-6 pb-2">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your restaurant settings
        </p>
      </div>

      <div className="mx-5 mt-4 flex flex-col gap-y-3">

        {/* ── Advanced Settings links ── */}
        {[
          { label: "Staff / Waiter Management", desc: "Add waiters, manage PINs and access", to: "/staff-settings" },
          { label: "Table Configuration", desc: "Add, edit and remove tables", to: "/table-settings" },
          { label: "Menu & Public Page", desc: "Manage menu categories and items", to: "/menu-settings" },
        ].map((item) => (
          <button
            key={item.to}
            onClick={() => navigate(item.to)}
            className="w-full flex items-center justify-between bg-white rounded-2xl shadow-card border border-gray-100 px-5 py-4"
          >
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">{item.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
            </div>
            <svg className="w-4 h-4 text-gray-300 flex-shrink-0 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ))}

        {/* ── Account / profile form ── */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5 mt-2">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Account</h2>
          <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">
                Company / Restaurant Name
              </label>
              <input
                value={userData.companyName}
                name="companyName"
                className={inputClass}
                placeholder="Your restaurant name"
                onChange={handleData}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">
                Country
              </label>
              <select
                value={userData.country}
                name="country"
                className={inputClass}
                onChange={handleData}
              >
                <option value="">Select country</option>
                {country.map((coun) => (
                  <option key={coun.name}>{coun.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-gray-900">
                  Mask customer names
                </p>
                <p className="text-xs text-gray-500">
                  Show token number instead of name on public display
                </p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setUserData({ ...userData, maskName: !userData.maskName })
                }
                className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                  userData.maskName ? "bg-primary" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                    userData.maskName ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <Button
              disabled={!isValid}
              type="submit"
              className={`w-full py-3 rounded-xl font-semibold text-sm mt-1 ${
                isValid
                  ? "bg-primary hover:bg-primary-light text-white"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Save Changes
            </Button>
          </form>
        </div>

        <Button
          onClick={handleLogout}
          className="w-full py-3 bg-white border border-red-200 text-red-500 text-sm font-semibold rounded-xl"
        >
          Log out
        </Button>

        <div className="mb-4" />
      </div>

      {open && (
        <Popup
          open={open}
          content="Data updated successfully!"
          buttonContent="Okay"
          onClose={onClose}
        />
      )}
      {loading && <Loader loading={loading} />}
    </div>
  );
}

export default Settings;
